import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  computeRoute,
  routeEta,
  toFuelLPerNm,
  formatDistanceUnit,
  formatDuration,
  formatFuel,
  formatNumber,
  formatClockTime,
} from '@boatnav/core';
import { useUnitsStore } from '../../app/unitsStore.js';
import { MapShell } from '../map/MapShell.js';
import { WaypointEditor } from './WaypointEditor.js';
import { RouteWeatherPanel } from './RouteWeatherPanel.js';
import { useBoatStore, activeBoat } from '../boat/boatStore.js';
import { useFollowStore } from './followStore.js';
import { computeAutoRoute } from './autoRoute.js';
import { autoRouteSupported } from './depthProvider.js';
import { useAutoRouteHandoff } from './autoRouteHandoff.js';
import { posStore } from '../nav/posStore.js';
import { listRoutes, putRoute, deleteRoute, type Route, type Waypoint } from '../../lib/db.js';
import { uuid } from '../../lib/uuid.js';
import { msg } from '../../lib/messages.js';
import './planner.css';

type AutoMode = 'off' | 'pickA' | 'pickB';

export function PlannerScreen(): JSX.Element {
  const navigate = useNavigate();
  const boat = useBoatStore(activeBoat);
  const boatLoaded = useBoatStore((s) => s.loaded);
  const loadBoats = useBoatStore((s) => s.load);
  const startFollow = useFollowStore((s) => s.start);
  const distanceUnit = useUnitsStore((s) => s.distance);

  const [waypoints, setWaypoints] = useState<Waypoint[]>([]);
  const [routeId, setRouteId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [saved, setSaved] = useState<Route[]>([]);
  const [showSaved, setShowSaved] = useState(false);
  const [selectedWp, setSelectedWp] = useState<number | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const [showList, setShowList] = useState(false);
  const [panTarget, setPanTarget] = useState<Waypoint | null>(null);

  // Auto-routing (spec 09).
  const [autoMode, setAutoMode] = useState<AutoMode>('off');
  const [autoA, setAutoA] = useState<Waypoint | null>(null);
  const [autoBusy, setAutoBusy] = useState(false);
  const [autoRouted, setAutoRouted] = useState(false);
  const [autoError, setAutoError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // A "Foreslå rute hit" B point handed off from the go-to chip (spec 13):
  // set while waiting for the user to tap A, consumed by the next tap.
  const pendingBRef = useRef<Waypoint | null>(null);
  const consumedHandoffRef = useRef(false);

  useEffect(() => {
    if (!boatLoaded) void loadBoats();
    void refreshSaved();
  }, [boatLoaded, loadBoats]);

  // Consume a pending go-to handoff exactly once, after boats have loaded
  // (runAuto needs `boat`). Guarded by a ref, not just the boatLoaded
  // dependency, so React StrictMode's double-effect in dev can't consume it
  // twice and lose it on the second (no-op) pass.
  useEffect(() => {
    if (!boatLoaded || consumedHandoffRef.current) return;
    consumedHandoffRef.current = true;
    const point = useAutoRouteHandoff.getState().consume();
    if (!point || !boat) return;
    setAutoError(null);
    const fix = posStore.getState().fix;
    if (fix) {
      void runAuto({ lat: fix.lat, lon: fix.lon }, point);
    } else {
      pendingBRef.current = point;
      setAutoA(null);
      setAutoMode('pickA');
    }
  }, [boatLoaded, boat]);

  const refreshSaved = async (): Promise<void> => setSaved(await listRoutes());

  const plan = useMemo(() => {
    if (!boat || waypoints.length < 2) return null;
    return computeRoute(waypoints, {
      cruiseKn: boat.cruiseKn,
      fuelLPerNm: toFuelLPerNm(boat.fuelValue, boat.fuelUnit, boat.cruiseKn),
    });
  }, [boat, waypoints]);

  const eta = plan ? routeEta(Date.now(), plan.totals.durationMin) : null;
  const fuelWarn =
    plan != null && boat?.tankL != null && boat.tankL > 0 && plan.totals.fuelL > 0.8 * boat.tankL;

  // Map taps: append a waypoint, or collect A/B when suggesting a route.
  const handleTap = (lat: number, lon: number): void => {
    if (autoMode === 'pickA') {
      if (pendingBRef.current) {
        // B was already handed off (spec 13) — this tap only needs to
        // supply A, then route immediately instead of waiting for a 2nd tap.
        const b = pendingBRef.current;
        pendingBRef.current = null;
        void runAuto({ lat, lon }, b);
      } else {
        setAutoA({ lat, lon });
        setAutoMode('pickB');
      }
    } else if (autoMode === 'pickB') {
      const a = autoA ?? { lat, lon };
      void runAuto(a, { lat, lon });
    } else {
      setWaypoints((prev) => [...prev, { lat, lon }]);
      setAutoRouted(false);
    }
  };

  const startSuggest = (): void => {
    if (!boat) return;
    setAutoError(null);
    const fix = posStore.getState().fix;
    if (fix) {
      setAutoA({ lat: fix.lat, lon: fix.lon });
      setAutoMode('pickB');
    } else {
      setAutoA(null);
      setAutoMode('pickA');
    }
  };

  const runAuto = async (a: Waypoint, b: Waypoint): Promise<void> => {
    if (!boat) return;
    setAutoMode('off');
    setAutoBusy(true);
    setAutoError(null);
    const controller = new AbortController();
    abortRef.current = controller;
    const res = await computeAutoRoute(a, b, boat, controller.signal);
    setAutoBusy(false);
    if ('waypoints' in res) {
      setWaypoints(res.waypoints.map((w) => ({ lat: w.lat, lon: w.lon })));
      setRouteId(null);
      setAutoRouted(true);
    } else if (res.error !== 'aborted') {
      setAutoError(
        res.error === 'no-path' || res.error === 'blocked-endpoint'
          ? msg.planner.failNoRoute
          : msg.planner.failGeneric,
      );
    }
  };

  const cancelAuto = (): void => {
    abortRef.current?.abort();
    pendingBRef.current = null;
    setAutoBusy(false);
    setAutoMode('off');
  };
  const moveWaypoint = (i: number, lat: number, lon: number): void => {
    setWaypoints((prev) => prev.map((w, idx) => (idx === i ? { ...w, lat, lon } : w)));
    setAutoRouted(false);
  };
  const deleteWaypoint = (i: number): void => {
    setWaypoints((prev) => prev.filter((_, idx) => idx !== i));
    setSelectedWp(null);
    setAutoRouted(false);
  };
  const insertAfter = (i: number): void => {
    setWaypoints((prev) => {
      const a = prev[i];
      const b = prev[i + 1] ?? prev[i];
      if (!a || !b) return prev;
      const mid = { lat: (a.lat + b.lat) / 2, lon: (a.lon + b.lon) / 2 };
      return [...prev.slice(0, i + 1), mid, ...prev.slice(i + 1)];
    });
    setSelectedWp(null);
    setAutoRouted(false);
  };

  // Waypoint list editor (spec 14B): ▲/▼ reorder is one splice, not DnD — the
  // wrong tool on wet screens and below the old-browser floor.
  const swapWaypoints = (i: number, j: number): void => {
    setWaypoints((prev) => {
      if (i < 0 || j < 0 || i >= prev.length || j >= prev.length) return prev;
      const next = [...prev];
      const tmp = next[i]!;
      next[i] = next[j]!;
      next[j] = tmp;
      return next;
    });
    setAutoRouted(false);
  };
  const moveWaypointUp = (i: number): void => swapWaypoints(i, i - 1);
  const moveWaypointDown = (i: number): void => swapWaypoints(i, i + 1);

  const clear = (): void => {
    setWaypoints([]);
    setRouteId(null);
    setName('');
    setAutoRouted(false);
    setAutoError(null);
    setAutoMode('off');
  };

  const save = async (): Promise<void> => {
    if (waypoints.length < 2) return;
    const route: Route = {
      id: routeId ?? uuid(),
      name: name.trim() || 'Rute',
      waypoints,
      updatedAt: Date.now(),
      deletedAt: null,
    };
    await putRoute(route);
    setRouteId(route.id);
    await refreshSaved();
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1200);
  };

  const loadRoute = (r: Route): void => {
    setWaypoints(r.waypoints);
    setRouteId(r.id);
    setName(r.name);
    setShowSaved(false);
  };

  const follow = (r: Route): void => {
    startFollow(r);
    navigate('/');
  };

  return (
    <div className="planner">
      <div className="planner__map">
        <MapShell>
          <WaypointEditor
            waypoints={waypoints}
            onAdd={handleTap}
            onMove={moveWaypoint}
            onSelect={setSelectedWp}
            panTo={panTarget}
          />
        </MapShell>
      </div>

      {(autoMode !== 'off' || autoBusy) && (
        <div className="planner__auto-overlay">
          {autoBusy ? (
            <div className="planner__auto-status">
              <span className="planner__spinner" /> {msg.planner.suggesting}
              <button type="button" className="planner__auto-cancel" onClick={cancelAuto}>
                {msg.planner.suggestCancel}
              </button>
            </div>
          ) : (
            <div className="planner__auto-status">
              {autoMode === 'pickA' ? msg.planner.pickStart : msg.planner.pickEnd}
              <button type="button" className="planner__auto-cancel" onClick={cancelAuto}>
                {msg.planner.suggestCancel}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="planner__sheet">
        {autoRouted && <div className="planner__auto-banner">⚠ {msg.planner.autoBanner}</div>}
        {autoError && <div className="planner__auto-error">{autoError}</div>}

        <div className="planner__row">
          <button
            type="button"
            className="planner__boatchip"
            onClick={() => navigate('/baat')}
          >
            {boat ? (
              <>
                <b>{boat.name}</b> · {formatNumber(boat.cruiseKn, 0)} kn ·{' '}
                {formatNumber(toFuelLPerNm(boat.fuelValue, boat.fuelUnit, boat.cruiseKn), 1)} l/nm
              </>
            ) : (
              msg.planner.noBoat
            )}
          </button>
          {waypoints.length >= 1 && (
            <button type="button" className="planner__link" onClick={() => setShowList(true)}>
              {msg.planner.list}
            </button>
          )}
          <button type="button" className="planner__link" onClick={() => setShowSaved(true)}>
            {msg.planner.savedRoutes}
          </button>
        </div>

        {autoRouteSupported() && boat && (
          <button
            type="button"
            className="btn btn--ghost planner__suggest"
            disabled={autoMode !== 'off' || autoBusy}
            onClick={startSuggest}
          >
            ✦ {msg.planner.suggest}
          </button>
        )}

        {waypoints.length < 2 ? (
          <p className="planner__hint">{msg.planner.tapToAdd}</p>
        ) : (
          <>
            <LegTable plan={plan} eta={eta} fuelWarn={fuelWarn} />
            {boat && plan && <RouteWeatherPanel waypoints={waypoints} plan={plan} />}
          </>
        )}

        {waypoints.length >= 2 && (
          <div className="planner__actions">
            <input
              className="planner__name"
              placeholder={msg.planner.routeName}
              value={name}
              maxLength={120}
              onChange={(e) => setName(e.target.value)}
            />
            <button type="button" className="btn btn--ghost" onClick={clear}>
              {msg.planner.clear}
            </button>
            <button type="button" className="btn btn--accent" onClick={() => void save()}>
              {savedFlash ? msg.planner.saved : msg.planner.save}
            </button>
          </div>
        )}
      </div>

      {selectedWp != null && (
        <div className="sheet-backdrop" onClick={() => setSelectedWp(null)}>
          <div className="sheet" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="sheet__handle" />
            <h2 className="sheet__title">
              {msg.planner.waypoint} {selectedWp + 1}
            </h2>
            <div className="sheet__actions">
              <button
                type="button"
                className="btn btn--ghost"
                disabled={selectedWp >= waypoints.length - 1}
                onClick={() => insertAfter(selectedWp)}
              >
                {msg.planner.insertAfter}
              </button>
              <button
                type="button"
                className="btn btn--accent"
                onClick={() => deleteWaypoint(selectedWp)}
              >
                {msg.planner.deleteWp}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSaved && (
        <div className="sheet-backdrop" onClick={() => setShowSaved(false)}>
          <div className="sheet" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="sheet__handle" />
            <h2 className="sheet__title">{msg.planner.savedRoutes}</h2>
            {saved.length === 0 ? (
              <p className="sheet__body">{msg.planner.empty}</p>
            ) : (
              <ul className="planner__saved">
                {saved.map((r) => (
                  <li key={r.id} className="saved-route">
                    <button type="button" className="saved-route__body" onClick={() => loadRoute(r)}>
                      <span className="saved-route__name">{r.name}</span>
                      <span className="saved-route__meta numeral">{r.waypoints.length} pkt</span>
                    </button>
                    <button type="button" className="saved-route__follow" onClick={() => follow(r)}>
                      {msg.planner.follow}
                    </button>
                    <button
                      type="button"
                      className="saved-route__del"
                      aria-label={msg.tracks.delete}
                      onClick={() => void deleteRoute(r.id).then(refreshSaved)}
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {showList && (
        <div className="sheet-backdrop" onClick={() => setShowList(false)}>
          <div className="sheet" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="sheet__handle" />
            <h2 className="sheet__title">{msg.planner.listTitle}</h2>
            <ul className="wp-list">
              {waypoints.map((wp, i) => {
                const leg = plan?.legs[i];
                return (
                  <li key={i} className="wp-list__row">
                    <button
                      type="button"
                      className="wp-list__body"
                      onClick={() => setPanTarget({ ...wp })}
                    >
                      <span className="wp-list__index numeral">{i + 1}</span>
                      <span className="wp-list__coords numeral">
                        {formatNumber(wp.lat, 4)}, {formatNumber(wp.lon, 4)}
                      </span>
                      {leg && (
                        <span className="wp-list__leg numeral">
                          {formatDistanceUnit(leg.distanceNm, distanceUnit)} →
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      className="wp-list__btn"
                      aria-label={msg.planner.moveUp}
                      disabled={i === 0}
                      onClick={() => moveWaypointUp(i)}
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      className="wp-list__btn"
                      aria-label={msg.planner.moveDown}
                      disabled={i === waypoints.length - 1}
                      onClick={() => moveWaypointDown(i)}
                    >
                      ▼
                    </button>
                    <button
                      type="button"
                      className="wp-list__btn wp-list__btn--del"
                      aria-label={msg.planner.deleteWp}
                      onClick={() => deleteWaypoint(i)}
                    >
                      ✕
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function LegTable({
  plan,
  eta,
  fuelWarn,
}: {
  plan: ReturnType<typeof computeRoute> | null;
  eta: number | null;
  fuelWarn: boolean;
}): JSX.Element | null {
  const [expanded, setExpanded] = useState(false);
  const distanceUnit = useUnitsStore((s) => s.distance);
  if (!plan) return null;
  const legs = expanded ? plan.legs : plan.legs.slice(0, 3);
  const hidden = plan.legs.length - legs.length;

  return (
    <div className="legs">
      <table className="legs__table">
        <thead>
          <tr>
            <th>{msg.planner.leg}</th>
            <th>{msg.planner.dist}</th>
            <th>{msg.planner.time}</th>
            <th>{msg.planner.fuel}</th>
          </tr>
        </thead>
        <tbody>
          {legs.map((leg, i) => (
            <tr key={i}>
              <td>{i + 1}</td>
              <td>{formatDistanceUnit(leg.distanceNm, distanceUnit)}</td>
              <td>{formatDuration(leg.durationMin)}</td>
              <td>{formatFuel(leg.fuelL)}</td>
            </tr>
          ))}
          {hidden > 0 && (
            <tr className="legs__more" onClick={() => setExpanded(true)}>
              <td colSpan={4}>+{hidden} …</td>
            </tr>
          )}
          <tr className="legs__total">
            <td>{msg.planner.total}</td>
            <td>{formatDistanceUnit(plan.totals.distanceNm, distanceUnit)}</td>
            <td>{formatDuration(plan.totals.durationMin)}</td>
            <td className={fuelWarn ? 'legs__fuel-warn' : undefined}>
              {formatFuel(plan.totals.fuelL)}
            </td>
          </tr>
        </tbody>
      </table>
      {fuelWarn && <div className="legs__warn">{msg.planner.fuelWarn}</div>}
      {eta != null && (
        <div className="legs__eta">
          {msg.planner.arrival}: <b className="numeral">{formatClockTime(eta)}</b>
        </div>
      )}
    </div>
  );
}
